import { useCallback } from "react";
import { Link } from "react-router";
import { User, LogOut } from "lucide-react";
import { useAuthStore } from "@/store/useAuthStore";
import { useOrganizationStore } from "@/store/useOrganizationStore";
import GTMService from "@/services/GTMService";
import { Constants } from "@/constants";
import { safeSessionRemoveItem } from "@/utils/safeStorage";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

export function ProfileDropdown() {
  const { logoutUser, organizationId, role } = useAuthStore();
  const organization = useOrganizationStore((state) => state.organization);

  const handleLogout = useCallback(() => {
    // Remove access token from session storage
    safeSessionRemoveItem("accessToken");
    // Call logout function from auth store which will clear the state
    logoutUser();
    // Track logout event with GTM
    GTMService.logoutEvent({
      organizationId: organizationId,
      role: role ? role.toString() : "unknown"
    });
    // Redirect to login page using window.location
    window.location.href = `${Constants.REACT_APP_API_BASE_URL}auth/login`;
  }, [logoutUser, organizationId, role]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center space-x-1 sm:space-x-2 focus:outline-none min-h-[44px] min-w-[44px]">
        <div className="flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-gray-100">
          <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
        </div>
        <div className="hidden md:flex flex-col items-start">
          <span className="text-sm font-medium">
            {organization?.name || "Admin"}
          </span>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="flex items-center gap-2 p-2">
          <div className="flex items-center justify-center h-10 w-10 rounded-full bg-gray-100">
            <User className="h-6 w-6 text-gray-600" />
          </div>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">
              {organization?.name || "Admin"}
            </p>
            <p className="text-xs text-muted-foreground">
              {organization?.owner?.email}
            </p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/user-profile" className="cursor-pointer flex items-center">
            <User className="mr-2 h-4 w-4" />
            <span>Profile</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer text-red-600"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
